/* eslint-disable consistent-return */
/* eslint-disable max-len */
/* eslint-disable no-restricted-syntax */

import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { init, SearchIndex } from 'emoji-mart';
import { Typography } from 'antd';
import styles from './styles.module.less';

const { Text } = Typography;

init({ data });

interface EmojiData {
    id: string;
    native: string;
}

interface EmojiPickerProps {
    onSelect: (emoji: EmojiData) => void;
    searchQuery?: string;
    inline?: boolean;
    onResultsCountChange?: (count: number) => void;
    onClose?: () => void;
}

const POPULAR_EMOJIS: EmojiData[] = [
    { id: 'thumbsup', native: '👍' },
    { id: 'clap', native: '👏' },
    { id: 'pray', native: '🙏' },
    { id: 'rocket', native: '🚀' },
    { id: 'heart', native: '❤️' },
    { id: 'grinning', native: '😀' },
    { id: 'smile', native: '😄' },
    { id: 'joy', native: '😂' },
    { id: 'heart_eyes', native: '😍' },
    { id: 'fire', native: '🔥' },
    { id: 'tada', native: '🎉' },
    { id: 'eyes', native: '👀' },
    { id: 'thinking', native: '🤔' },
    { id: 'sob', native: '😭' },
    { id: 'sweat_smile', native: '😅' },
    { id: 'ok_hand', native: '👌' },
    { id: 'raised_hands', native: '🙌' },
    { id: 'muscle', native: '💪' },
    { id: 'check', native: '✅' },
    { id: 'x', native: '❌' },
  ];

export function EmojiPicker({ onSelect, searchQuery, inline, onResultsCountChange, onClose }: EmojiPickerProps) {
    const [open, setIsOpen] = useState(false);
    const [searchResults, setSearchResults] = useState<EmojiData[]>(inline ? POPULAR_EMOJIS : []);
    const [activeIndex, setActiveIndex] = useState(0);

    const pickerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleSearch = async (value: string) => {
        if (value.length === 0) {
            setSearchResults(POPULAR_EMOJIS);
            setActiveIndex(0);
            onResultsCountChange?.(POPULAR_EMOJIS.length);
            return;
        }

        const emoticon = `:${value}`;
        const hasUppercase = value !== value.toLowerCase();

        if (hasUppercase) {
            // Uppercase query → emoticon-only search (IDs/keywords are always lowercase)
            const emoticonResults: EmojiData[] = [];
            const allEmojis = (data as any).emojis as Record<string, any>;
            for (const emoji of Object.values(allEmojis)) {
                if ((emoji as any).emoticons?.includes(emoticon)) {
                    const native = (emoji as any).skins?.[0]?.native;
                    if (native) {
                        emoticonResults.push({ id: (emoji as any).id, native });
                    }
                }
            }
            setSearchResults(emoticonResults);
            setActiveIndex(0);
            onResultsCountChange?.(emoticonResults.length);
            return;
        }

        // All-lowercase query → keyword/name/id search + emoticon scan merged
        const emojis = await SearchIndex.search(value);
        const normalResults: EmojiData[] = emojis
            ? emojis.slice(0, 100).map((emoji: any) => ({
                  id: emoji.id,
                  native: emoji.skins[0].native,
              }))
            : [];

        const seenIds = new Set(normalResults.map((e) => e.id));
        const emoticonResults: EmojiData[] = [];

        const allEmojis = (data as any).emojis as Record<string, any>;
        for (const emoji of Object.values(allEmojis)) {
            if ((emoji as any).emoticons?.includes(emoticon) && !seenIds.has((emoji as any).id)) {
                const native = (emoji as any).skins?.[0]?.native;
                if (native) {
                    emoticonResults.push({ id: (emoji as any).id, native });
                    seenIds.add((emoji as any).id);
                }
            }
        }

        // Emoticon exact-matches come first, then keyword results
        const combined = [...emoticonResults, ...normalResults];
        setSearchResults(combined);
        setActiveIndex(0);
        onResultsCountChange?.(combined.length);
    };

    const selectEmoji = useCallback((emoji: EmojiData) => {
        onSelect(emoji);
        setSearchResults([]);
        setActiveIndex(0);
    }, [onSelect]);

    useEffect(() => {
        if (searchQuery !== undefined) {
            handleSearch(searchQuery);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (inline && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const activeElement = container.children[activeIndex] as HTMLElement;

            if (activeElement) {
                activeElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [activeIndex, inline]);

    useEffect(() => {
        if (!inline || searchResults.length === 0) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                if (searchResults[activeIndex]) {
                    selectEmoji(searchResults[activeIndex]);
                }
            } else if (e.key === 'Escape') {
                e.stopPropagation();
                setSearchResults([]);
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [inline, searchResults, activeIndex, selectEmoji]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    if (inline) {
        if (searchResults.length === 0) return null;
        return (
            <div className={styles.listWrapper}>
                <div className={styles.listContainer} ref={scrollContainerRef}>
                    {searchResults.map((emoji, index) => (
                        <div
                            key={emoji.id}
                            onClick={() => selectEmoji(emoji)}
                            className={`${styles.listItem} ${index === activeIndex ? styles.activeItem : ''}`}
                            title={emoji.id}
                        >
                            <span>{emoji.native}</span>
                            <Text ellipsis style={{ flex: 1 }}>:{emoji.id}</Text>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <Picker
            theme="light"
            data={data}
            onEmojiSelect={(emoji: any) => {
                onSelect(emoji);
                setIsOpen(false);
            }}
        />
    );
}
